/*
#############################################################################
# Copyright (c) 2022-2023 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
#
# NVIDIA CORPORATION, its affiliates and licensors retain all intellectual
# property and proprietary rights in and to this material, related
# documentation and any modifications thereto. Any use, reproduction,
# disclosure or distribution of this material and related documentation
# without an express license agreement from NVIDIA CORPORATION or
# its affiliates is strictly prohibited.
###############################################################################
*/

/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import { useState, useEffect } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { webSocketActions } from "./websocketSlice";
import config from "../../config/config";
import { deleteCart } from "../../features/cart/deleteCartSlice";
import { cartActions, getCart } from "../../features/cart/CartSlice";
import { ovSliceActions } from "../../features/bot-state/ovSlice";
import { asrSliceActions } from "../../features/asrText/asrSlice";
import { ttsSliceActions } from "../../features/ttsText/ttsSlice";
import useInterval from "../../utils/useInterval";

const websocketErrorToString = (event) => {
  let reason = "";
  if (event.code === 1000)
  reason = "Normal closure, meaning that the purpose for which the connection was established has been fulfilled.";
  else if(event.code === 1001)
    reason = "An endpoint is \"going away\", such as a server going down or a browser having navigated away from a page.";
  else if(event.code === 1002)
    reason = "An endpoint is terminating the connection due to a protocol error";
  else if(event.code === 1003)
    reason = "An endpoint is terminating the connection because it has received a type of data it cannot accept (e.g., an endpoint that understands only text data MAY send this if it receives a binary message).";
  else if(event.code === 1004)
    reason = "Reserved. The specific meaning might be defined in the future.";
  else if(event.code === 1005)
    reason = "No status code was actually present.";
  else if(event.code === 1006)
  reason = "The connection was closed abnormally, e.g., without sending or receiving a Close control frame";
  else if(event.code === 1007)
    reason = "An endpoint is terminating the connection because it has received data within a message that was not consistent with the type of the message (e.g., non-UTF-8 [https://www.rfc-editor.org/rfc/rfc3629] data within a text message).";
  else if(event.code === 1008)
    reason = "An endpoint is terminating the connection because it has received a message that \"violates its policy\". This reason is given either if there is no other sutible reason, or if there is a need to hide specific details about the policy.";
  else if(event.code === 1009)
  reason = "An endpoint is terminating the connection because it has received a message that is too big for it to process.";
  else if(event.code === 1010) // Note that this status code is not used by the server, because it can fail the WebSocket handshake instead.
    reason = `An endpoint (client) is terminating the connection because it has expected the server to negotiate one or more extension, but the server didn't return them in the response message of the WebSocket handshake. <br /> Specifically, the extensions that are needed are: ${  event.reason}`;
  else if(event.code === 1011)
    reason = "A server is terminating the connection because it encountered an unexpected condition that prevented it from fulfilling the request.";
  else if(event.code === 1015)
    reason = "The connection was closed due to a failure to perform a TLS handshake (e.g., the server certificate can't be verified).";
  else
    reason = "Unknown reason";
  return reason;
}

function WebSocketConnection() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [socketUrl, setSocketUrl] = useState(`${config.websocketEndpoint}`);
  
  /* Initiate websocket connection */
  const streamId = useSelector((state) => state.uuid.streamId);
  const sessionActive = useSelector((state) => state.uuid.sessionActive);
  const shouldReconnect = useSelector((state) => state.uuid.shouldReconnect);
  
  useEffect(() => {
    if(streamId !== "") {
      setSocketUrl(`${config.websocketEndpoint}?stream_id=${streamId}&is_camera_enabled=${config.enableCamera}`);
      dispatch(webSocketActions.setShouldReconnect(true));
    }
  }, [streamId]);

  const { sendMessage, lastJsonMessage, readyState } = useWebSocket(
    socketUrl,
    {
      onError: (e) => console.error("websocket error occured", e),
      onClose: (e) => console.error(websocketErrorToString(e)),
      share: true,
      retryOnError: true,
      reconnectAttempts: Infinity,
      reconnectInterval: config.websocketReconnectInterval,
      shouldReconnect: () => true,
      onReconnectStop: () => {
        console.log("Max attempt reached");
        dispatch(webSocketActions.setWebsocket({ uuid: "", status: 3 }));
      },
    },
    shouldReconnect
  );

  const itemsInCart = useSelector((state) => state.cart.data);
  const totalOrderCost = useSelector((state) => state.cart.totalBill);

  useEffect(() => {
    if(!sessionActive) {
      dispatch(cartActions.emptyCart());
      if(location.pathname.toUpperCase() !== `/${config.homeView.toUpperCase()}`) {
        navigate(`/${config.homeView.toUpperCase()}`);
      }
    }
    
  }, [sessionActive]);
    
  useEffect(() => {
    dispatch(webSocketActions.setWebsocket({ uuid: "", status: readyState }));
  }, [readyState]);

  useInterval(() => {
    sendMessage('ping')
  }, config.websocketPingInterval);

  useEffect(() => {
    console.log("received a new message from WS: ", lastJsonMessage)
    const actionHandler = (message) => {
      console.debug("handle VIEW action on message", message);
      if (message.action === "action_welcome") {
        if (message.view != null) {
          if (location.pathname === `/${message.view}`) {
            navigate(`/${message.view}`, { replace: true });
          } else {
            navigate(`/${message.view}`);
          }
        }
      } else if (message.action === "action_add_items") {
        if (message.is_update_cart) {
          dispatch(getCart());
        }
        if (message.view != null) {
          localStorage.setItem(
            `/${message.view}`,
            JSON.stringify(message.content.recommended_items)
          );
          if (location.pathname === `/${message.view}`) {
            navigate(`/${message.view}`, { replace: true });
          } else {
            navigate(`/${message.view}`);
          }
        }
      } else if (message.action === "action_revert_last") {
        if (message.is_update_cart) {
          dispatch(getCart());
        }
      } else if (message.action === "action_display_items") {
        if (
          message.content.displayed_items.length === 1 ||
          message.view === "ITEM_VIEW"
        ) {
          const size = {
            price: message.content.displayed_items[0].price ? message.content.displayed_items[0].price : 0,
            name: message.content.displayed_items[0].size && message.content.displayed_items[0].size[0] ? message.content.displayed_items[0].size[0] : "",
            calories: message.content.displayed_items[0].calories ? message.content.displayed_items[0].calories : 0,
          }
          if(message.content.displayed_items[0].sizes && message.content.displayed_items[0].sizes[0]) {
            if(message.content.displayed_items[0].sizes[0].price) {
              size.price = message.content.displayed_items[0].sizes[0].price;
            }
            if(message.content.displayed_items[0].sizes[0].name) {
              size.name = message.content.displayed_items[0].sizes[0].name;
            }
            if(message.content.displayed_items[0].sizes[0].calories) {
              size.calories = message.content.displayed_items[0].sizes[0].calories;
            }
          }
          const itemData = {
            image_loc: message.content.displayed_items[0].image_loc,
            item_id: message.content.displayed_items[0].item_id,
            name: message.content.displayed_items[0].name,
            price: size.price,
            quantity: 1,
            size: size.name,
            toppings: message.content.displayed_items[0].toppings,
            calories: size.calories,
            description: message.content.displayed_items[0].description,
          };
          localStorage.setItem(
            `/ITEM_VIEW/${message.content.displayed_items[0].item_id}`,
            JSON.stringify(itemData)
          );
          if (
            location.pathname ===
            `/ITEM_VIEW/${message.content.displayed_items[0].item_id}`
          ) {
            navigate(
              `/ITEM_VIEW/${message.content.displayed_items[0].item_id}`,
              { replace: true }
            );
          } else {
            navigate(
              `/ITEM_VIEW/${message.content.displayed_items[0].item_id}`
            );
          }
        } else if (message.view === "VARIETY_VIEW") {
          localStorage.setItem(
            "/FILTER_VIEW/variety",
            JSON.stringify(message.content)
          );
          if (location.pathname === "/FILTER_VIEW/variety") {
            navigate(`/FILTER_VIEW/variety`, { replace: true });
          } else {
            navigate(`/FILTER_VIEW/variety`);
          }
        } else if (message.view === "RECOMMENDED_VIEW") {
          localStorage.setItem(
            "/RECOMMENDED_VIEW",
            JSON.stringify(message.content)
          );
          if (location.pathname === "/RECOMMENDED_VIEW") {
            navigate(`/RECOMMENDED_VIEW`, { replace: true });
          } else {
            navigate(`/RECOMMENDED_VIEW`);
          }
        } else if (message.view === "CATEGORY_VIEW") {
          const categories = message.content.filters.values.join("-");
          console.debug("message.content", message.content);
          localStorage.setItem(
            `/FILTER_VIEW/category/${categories}`,
            JSON.stringify(message.content)
          );
          if (location.pathname === `/FILTER_VIEW/category/${categories}`) {
            navigate(`/FILTER_VIEW/category/${categories}`, { replace: true });
          } else {
            navigate(`/FILTER_VIEW/category/${categories}`);
          }
        }
      } else if (message.action === "action_display_cart") {
        if (location.pathname === `/CART_VIEW`) {
          navigate(`/CART_VIEW`, { replace: true });
        } else {
          navigate(`/CART_VIEW`);
        }
      } else if (
        message.action === "action_remove_items" ||
        message.action === "action_replace_items" ||
        message.action === "action_add_toppings" ||
        message.action === "action_remove_toppings" ||
        message.action === "action_replace_toppings" ||
        message.action === "action_clear_cart" ||
        message.action === "action_agent_handover"
      ) {
        if (message.is_update_cart) {
          dispatch(getCart());
        }
        if (message.view != null) {
          if (location.pathname === `/${message.view}`) {
            navigate(`/${message.view}`, { replace: true });
          } else {
            navigate(`/${message.view}`);
          }
        }
      } else if (message.action === "action_complete_order") {
        window.cart = itemsInCart;
        window.totalOrderCost = totalOrderCost;
        if (message.is_update_cart) {
          dispatch(getCart());
        }
        if (message.view != null) {
          if (location.pathname === `/${message.view}`) {
            navigate(`/${message.view}`, {
              replace: true,
              state: { showFeedback: true },
            });
          } else {
            navigate(`/${message.view}`, { state: { showFeedback: true } });
          }
        }
        enqueueSnackbar("Order successful", { variant: "success" });
      } else if (message.action === "action_last_page") {
        navigate(-1);
      } else if (message.action === "action_next_page") {
        navigate(1);
      } else if (message.action === "fovExit") {
        dispatch(webSocketActions.setSessionActive(false));
      } else if (message.action === "fovEntry") {
        dispatch(webSocketActions.setSessionActive(true));
        if (message.session_id != null) {
          dispatch(webSocketActions.setSessionId(message.session_id));
        }
        dispatch(getCart());
      }

      if(Object.prototype.hasOwnProperty.call(message, "node")){
        console.debug("custom view message content", message);
        sessionStorage.setItem(
          `/CUSTOM_VIEW`,
          JSON.stringify(message)
        );
        if (location.pathname === `/CUSTOM_VIEW`) {
          navigate(`/CUSTOM_VIEW`, { replace: true });
        } else {
          navigate(`/CUSTOM_VIEW`);
        }
      }

      // update TTS state
      if (Object.prototype.hasOwnProperty.call(message, "tts")) {
        dispatch(ttsSliceActions.setTtsState(message.tts));
      }

      // update ASR state
      if (Object.prototype.hasOwnProperty.call(message, "asr")) {
        dispatch(asrSliceActions.setAsrState(message.asr));
      }

      // update OV state
      if (Object.prototype.hasOwnProperty.call(message, "ov_state")) {
        if (message.ov_state.toUpperCase() === "WAIT_FOR_TRIGGER") {
          dispatch(ovSliceActions.setOvState("WAITING"));
        } else if (message.ov_state.toUpperCase() === "ASR_ACTIVE") {
          dispatch(ovSliceActions.setOvState("LISTENING"));
        } else if (message.ov_state.toUpperCase() === "TTS_ACTIVE") {
          dispatch(ovSliceActions.setOvState("SPEAKING"));
        } else {
          dispatch(ovSliceActions.setOvState(message.ov_state));
        }
      }

      if (Object.prototype.hasOwnProperty.call(message, "type")) {
        switch (message.type.toLowerCase()){
          case ("wdm_event"):
            if(message.status === 'up'){
              enqueueSnackbar("System is up again.  Please refresh to continue", {variant: "success"});
            } else if(message.status === "down"){
              enqueueSnackbar("There was a connection error with the Tokkio application that has been resolved.  In order to continue, you must restart the application.", {variant: "error"});
            }
            break;
          default:
            break;
        }
      }
    };

    if (lastJsonMessage !== null) {
      console.debug("lastJsonMessage: ", lastJsonMessage);
      /* Set UUID if present. Otherwise set items to display */
      if (Object.prototype.hasOwnProperty.call(lastJsonMessage, "client_id")) {
        const clientId = lastJsonMessage.client_id;
        // console.info("client id: ", clientId);
        dispatch(
          webSocketActions.setWebsocket({ uuid: clientId, status: readyState })
        );
        /* delete cart from previous connection since multi client is not supported yet */
        dispatch(deleteCart());
      } else if (
        Object.prototype.hasOwnProperty.call(lastJsonMessage, "action") ||
        Object.prototype.hasOwnProperty.call(lastJsonMessage, "tts") ||
        Object.prototype.hasOwnProperty.call(lastJsonMessage, "asr") ||
        Object.prototype.hasOwnProperty.call(lastJsonMessage, "ov_state") || 
        Object.prototype.hasOwnProperty.call(lastJsonMessage, "node") || 
        Object.prototype.hasOwnProperty.call(lastJsonMessage, "type")
      ) {
        actionHandler(lastJsonMessage);
      } else if (Object.prototype.hasOwnProperty.call(lastJsonMessage, "fovEntry") && lastJsonMessage.fovEntry !== '') {
        dispatch(webSocketActions.setSessionActive(true));
        dispatch(webSocketActions.setSessionId(lastJsonMessage.fovEntry));
        dispatch(getCart());
      } else {
        console.log("Unrecognized websocket message: ", lastJsonMessage);
      }
    }
  }, [lastJsonMessage]);
}

export default WebSocketConnection;
